import { Panel } from '@app/components/base/content-structure/Panel';
import { Box, Divider, Grid, Skeleton, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { usePreviewFragment } from '../hooks/usePreviewFragment';
import { AudioSkeleton } from './media-detail/audio/AudioSkeleton';
import { VideoSkeleton } from './media-detail/video/VideoSkeleton';
import { useTranscriptionPanelViewportMaxHeight } from './transcription/hooks/useTranscriptionPanelViewportMaxHeight';
import { TranscriptionSkeleton } from './transcription/panel/state/TranscriptionSkeleton';

/**
 * Skeleton for the fragment view content.
 * Mirrors FragmentMediaDetail: metadata, player, logger actions, and description in the
 * main column; transcription in a bordered panel. Audio vs video comes from navigation state.
 */
export function FragmentViewContentSkeleton() {
  const { fragmentFromNavigation } = usePreviewFragment();
  const theme = useTheme();
  const isMdOrLower = useMediaQuery(theme.breakpoints.down('lg'));
  const transcriptionPanelMaxHeight = useTranscriptionPanelViewportMaxHeight();

  const player =
    fragmentFromNavigation?.mediaType === 'AUDIO' ? (
      <AudioSkeleton showLoader={false} />
    ) : (
      <VideoSkeleton showLoader={false} />
    );

  const metadata = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Skeleton variant="text" width={148} height={20} />
        <Skeleton variant="text" width={72} height={20} />
        <Skeleton variant="text" width={48} height={20} />
      </Stack>
      <Skeleton variant="text" width={120} height={20} />
    </Box>
  );

  const mediaBlock = (
    <Box>
      {player}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5 }}>
        <Skeleton variant="text" width={108} height={20} />
        <Skeleton variant="rounded" width={88} height={30} />
        <Skeleton variant="rounded" width={72} height={30} />
        <Skeleton variant="rounded" width={112} height={30} />
      </Stack>
      <Divider sx={{ borderBottomWidth: '0.5px', mt: 1.5 }} />
    </Box>
  );

  const description = (
    <Box>
      <Skeleton variant="text" width="100%" height={20} />
      <Skeleton variant="text" width="68%" height={20} />
    </Box>
  );

  const transcriptionPanel = (
    <Panel
      title={<Skeleton variant="text" width={108} />}
      actions={
        <Stack direction="row" spacing={0.5}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
        </Stack>
      }
      sx={{
        height: '100%',
        maxHeight: transcriptionPanelMaxHeight,
      }}
    >
      <TranscriptionSkeleton rowCount={8} />
    </Panel>
  );

  if (isMdOrLower) {
    return (
      <Stack spacing={2}>
        {metadata}
        {mediaBlock}
        {transcriptionPanel}
        {description}
      </Stack>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={8}>
        <Stack spacing={2}>
          {metadata}
          {mediaBlock}
          {description}
        </Stack>
      </Grid>
      <Grid item xs={12} lg={4}>
        {transcriptionPanel}
      </Grid>
    </Grid>
  );
}
